import { useRef, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import DOMPurify from 'dompurify';

// Stored descriptions are HTML written by any project member (and by the
// meeting import), so they are sanitized before reaching innerHTML. This also
// neutralizes markup saved before this check existed.
const sanitizeHtml = (html) => DOMPurify.sanitize(html || '', { ADD_ATTR: ['target'] });

const escapeHtml = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

const TOOLBAR_BUTTONS = [
  { command: 'bold', icon: 'B', title: 'Negrita (Ctrl+B)', style: { fontWeight: 'bold' } },
  { command: 'italic', icon: 'I', title: 'Cursiva (Ctrl+I)', style: { fontStyle: 'italic' } },
  { command: 'underline', icon: 'U', title: 'Subrayado (Ctrl+U)', style: { textDecoration: 'underline' } },
  { command: 'strikeThrough', icon: 'S', title: 'Tachado', style: { textDecoration: 'line-through' } },
  { type: 'separator' },
  { command: 'insertUnorderedList', icon: '•', title: 'Lista con viñetas' },
  { command: 'insertOrderedList', icon: '1.', title: 'Lista numerada' },
  { type: 'separator' },
  { command: 'formatBlock', value: 'h3', icon: 'H', title: 'Encabezado' },
  { command: 'formatBlock', value: 'blockquote', icon: '"', title: 'Cita' },
  { command: 'createLink', icon: '🔗', title: 'Insertar enlace', isLink: true },
  { type: 'separator' },
  { command: 'removeFormat', icon: '⊘', title: 'Quitar formato' },
];

const FILE_TYPE_ICONS = {
  'image/': '🖼️',
  'application/pdf': '📄',
  'application/msword': '📝',
  'application/vnd.openxmlformats-officedocument.wordprocessingml': '📝',
  'application/vnd.ms-excel': '📊',
  'application/vnd.openxmlformats-officedocument.spreadsheetml': '📊',
  'text/': '📃',
  'application/zip': '📦',
  default: '📎',
};

function getFileIcon(mimeType) {
  for (const [key, icon] of Object.entries(FILE_TYPE_ICONS)) {
    if (key !== 'default' && mimeType?.startsWith(key)) {
      return icon;
    }
  }
  return FILE_TYPE_ICONS.default;
}

function isImageType(mimeType) {
  return mimeType?.startsWith('image/');
}

export default function RichTextEditor({
  value = '',
  onChange,
  placeholder = 'Escribe aquí...',
  minHeight = 120,
  disabled = false,
  attachments = [],
}) {
  const editorRef = useRef(null);
  const isInternalChange = useRef(false);
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);

  useEffect(() => {
    if (editorRef.current && !isInternalChange.current) {
      const safe = sanitizeHtml(value);
      if (editorRef.current.innerHTML !== safe) {
        editorRef.current.innerHTML = safe;
      }
    }
    isInternalChange.current = false;
  }, [value]);

  const handleInput = useCallback(() => {
    if (editorRef.current && onChange) {
      isInternalChange.current = true;
      const content = editorRef.current.innerHTML;
      onChange(content === '<br>' ? '' : content);
    }
  }, [onChange]);

  const execCommand = useCallback((command, value = null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
    handleInput();
  }, [handleInput]);

  const insertAttachmentReference = useCallback((attachment) => {
    editorRef.current?.focus();

    const isImage = isImageType(attachment.fileType);
    const icon = getFileIcon(attachment.fileType);
    // The file name is chosen by whoever uploaded it: escape before building markup.
    const filename = escapeHtml(attachment.filename);
    const fileUrl = escapeHtml(attachment.fileUrl);

    let html;
    if (isImage) {
      // Para imágenes, insertar una miniatura con enlace
      html = `<a href="${fileUrl}" target="_blank" rel="noopener noreferrer" class="attachment-ref attachment-image" style="display: inline-block; margin: 4px 0; padding: 8px; background: #f1f5f9; border-radius: 8px; text-decoration: none; border: 1px solid #e2e8f0;">
        <img src="${fileUrl}" alt="${filename}" style="max-width: 200px; max-height: 150px; border-radius: 4px; display: block;" />
        <span style="display: block; font-size: 11px; color: #64748b; margin-top: 4px;">${filename}</span>
      </a>&nbsp;`;
    } else {
      // Para otros archivos, insertar un badge con enlace
      html = `<a href="${fileUrl}" target="_blank" rel="noopener noreferrer" class="attachment-ref" style="display: inline-flex; align-items: center; gap: 6px; padding: 4px 10px; background: linear-gradient(135deg, #667eea15 0%, #764ba215 100%); border: 1px solid #667eea30; border-radius: 6px; text-decoration: none; font-size: 13px; color: #667eea; margin: 2px;">
        <span>${icon}</span>
        <span style="max-width: 150px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${filename}</span>
      </a>&nbsp;`;
    }

    document.execCommand('insertHTML', false, html);
    handleInput();
    setShowAttachmentMenu(false);
  }, [handleInput]);

  const handleToolbarClick = useCallback((btn) => {
    if (btn.isLink) {
      const url = prompt('Ingresa la URL del enlace:', 'https://');
      if (url) {
        execCommand('createLink', url);
      }
    } else if (btn.value) {
      execCommand(btn.command, btn.value);
    } else {
      execCommand(btn.command);
    }
  }, [execCommand]);

  const handleKeyDown = useCallback((e) => {
    if (e.ctrlKey || e.metaKey) {
      switch (e.key.toLowerCase()) {
        case 'b':
          e.preventDefault();
          execCommand('bold');
          break;
        case 'i':
          e.preventDefault();
          execCommand('italic');
          break;
        case 'u':
          e.preventDefault();
          execCommand('underline');
          break;
      }
    }
  }, [execCommand]);

  const handlePaste = useCallback((e) => {
    e.preventDefault();
    // Clipboard HTML can come from any web page; plain text must not be parsed as markup.
    const html = e.clipboardData.getData('text/html');
    const content = html
      ? sanitizeHtml(html)
      : escapeHtml(e.clipboardData.getData('text/plain')).replace(/\n/g, '<br>');
    document.execCommand('insertHTML', false, content);
    handleInput();
  }, [handleInput]);

  const containerStyle = {
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    overflow: 'hidden',
    backgroundColor: disabled ? '#f1f5f9' : '#ffffff',
    opacity: disabled ? 0.7 : 1,
    position: 'relative',
  };

  const toolbarStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
    padding: '8px 12px',
    backgroundColor: '#f8fafc',
    borderBottom: '1px solid #e2e8f0',
    flexWrap: 'wrap',
  };

  const buttonStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    border: 'none',
    borderRadius: '6px',
    backgroundColor: 'transparent',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: '14px',
    color: '#475569',
    transition: 'all 0.15s ease',
  };

  const buttonHoverStyle = {
    backgroundColor: '#e2e8f0',
    color: '#1e293b',
  };

  const separatorStyle = {
    width: '1px',
    height: '24px',
    backgroundColor: '#e2e8f0',
    margin: '0 6px',
  };

  const editorStyle = {
    minHeight: `${minHeight}px`,
    padding: '12px 16px',
    outline: 'none',
    fontSize: '14px',
    lineHeight: '1.6',
    color: '#1e293b',
    backgroundColor: disabled ? '#f1f5f9' : '#ffffff',
  };

  const placeholderStyle = {
    color: '#94a3b8',
    pointerEvents: 'none',
    position: 'absolute',
    top: '12px',
    left: '16px',
    fontSize: '14px',
  };

  const attachmentMenuStyle = {
    position: 'absolute',
    top: '44px',
    right: '12px',
    backgroundColor: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    padding: '8px 0',
    zIndex: 100,
    minWidth: '220px',
    maxHeight: '250px',
    overflowY: 'auto',
  };

  const attachmentItemStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 14px',
    cursor: 'pointer',
    transition: 'background 0.15s',
    border: 'none',
    background: 'none',
    width: '100%',
    textAlign: 'left',
    fontSize: '13px',
  };

  const showPlaceholder = !value || value === '<br>';
  const hasAttachments = attachments && attachments.length > 0;

  return (
    <div style={containerStyle}>
      <div style={toolbarStyle}>
        {TOOLBAR_BUTTONS.map((btn, index) => {
          if (btn.type === 'separator') {
            return <div key={index} style={separatorStyle} />;
          }
          return (
            <button
              key={index}
              type="button"
              title={btn.title}
              disabled={disabled}
              onClick={() => handleToolbarClick(btn)}
              style={buttonStyle}
              onMouseEnter={(e) => {
                if (!disabled) {
                  Object.assign(e.target.style, buttonHoverStyle);
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#475569';
              }}
            >
              <span style={btn.style}>{btn.icon}</span>
            </button>
          );
        })}

        {/* Attachment reference button */}
        {hasAttachments && (
          <>
            <div style={separatorStyle} />
            <button
              type="button"
              title="Insertar referencia a adjunto"
              disabled={disabled}
              onClick={() => setShowAttachmentMenu(!showAttachmentMenu)}
              style={{
                ...buttonStyle,
                width: 'auto',
                padding: '0 10px',
                gap: '4px',
                backgroundColor: showAttachmentMenu ? '#667eea15' : 'transparent',
                color: showAttachmentMenu ? '#667eea' : '#475569',
              }}
              onMouseEnter={(e) => {
                if (!disabled && !showAttachmentMenu) {
                  Object.assign(e.target.style, buttonHoverStyle);
                }
              }}
              onMouseLeave={(e) => {
                if (!showAttachmentMenu) {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#475569';
                }
              }}
            >
              <span>📎</span>
              <span style={{ fontSize: '12px' }}>Adjuntos</span>
              <span style={{ fontSize: '10px' }}>▼</span>
            </button>
          </>
        )}
      </div>

      {/* Attachment dropdown menu */}
      {showAttachmentMenu && hasAttachments && (
        <div style={attachmentMenuStyle}>
          <div style={{ padding: '6px 14px', fontSize: '11px', color: '#94a3b8', fontWeight: '600', textTransform: 'uppercase' }}>
            Insertar referencia
          </div>
          {attachments.map((attachment) => (
            <button
              key={attachment.id}
              type="button"
              style={attachmentItemStyle}
              onClick={() => insertAttachmentReference(attachment)}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f8fafc';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <span style={{ fontSize: '18px' }}>{getFileIcon(attachment.fileType)}</span>
              <span style={{
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: '#334155',
              }}>
                {attachment.filename}
              </span>
              {isImageType(attachment.fileType) && (
                <span style={{ fontSize: '10px', color: '#94a3b8' }}>IMG</span>
              )}
            </button>
          ))}
        </div>
      )}

      <div style={{ position: 'relative' }}>
        {showPlaceholder && <div style={placeholderStyle}>{placeholder}</div>}
        <div
          ref={editorRef}
          contentEditable={!disabled}
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onBlur={() => setTimeout(() => setShowAttachmentMenu(false), 200)}
          style={editorStyle}
          suppressContentEditableWarning
        />
      </div>
    </div>
  );
}

RichTextEditor.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  minHeight: PropTypes.number,
  disabled: PropTypes.bool,
  attachments: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    filename: PropTypes.string,
    fileUrl: PropTypes.string,
    fileType: PropTypes.string,
  })),
};
